import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { User as UserIcon, Users } from "lucide-react";
import { SUB_STATUSES, subMeta } from "../lib/pipeline";
import { visibleJobSubStatuses } from "../lib/jobTypes";

// Pure scheduling / handoff states — nobody physically works these, so
// asking the office to pick a staff member for them is just noise.
// Design gates (awaiting_design → design_in_progress → design_approved) DO
// take an owner (the designer), so they stay.
const NO_STAFF_STAGES = new Set([
  "unbooked", "booked", "in_store",
  "ready_for_collection", "collected",
]);

/**
 * Per-stage staff assignment editor.
 *
 * Lists only the stages that (a) actually appear on THIS job's pipeline
 * (derived from the installations picked) AND (b) require someone to own
 * the work. So a full colour-change wrap shows Wash / Disassembly / Clay bar
 * / Wrapping / Assembly / Quality check; a business-branding job additionally
 * shows the three design stages. Scheduling states (Unbooked/Booked/etc) are
 * hidden — they don't need a name against them.
 *
 * Backend endpoint: `PUT /api/jobs/{id}/stage-installer`
 * with `{stage, installer_id}` — passing an empty installer_id clears it.
 *
 * Data source: `job.stage_installers` (map<stage, {id, name}>).
 */
export default function JobStageAssignments({ job, canEdit = true, onChange }) {
  const [staff, setStaff] = useState([]);
  const [saving, setSaving] = useState("");

  useEffect(() => {
    // Roster endpoint is open to office+admin; returns just installer/
    // assistant accounts so office can reassign without needing the full
    // admin user-management scope.
    api.get("/users/roster")
      .then(({ data }) => setStaff(data))
      .catch(() => setStaff([]));
  }, []);

  // Derive the stages actually relevant to THIS job (adaptive to its
  // installations), then drop the non-work slots.
  const stages = useMemo(() => {
    const relevantValues = visibleJobSubStatuses(job);
    const relevantSet = new Set(relevantValues);
    return SUB_STATUSES.job.filter((s) =>
      relevantSet.has(s.value) && !NO_STAFF_STAGES.has(s.value)
    );
  }, [job]);
  const map = job.stage_installers || {};
  const currentSub = job.sub_status;

  const setStage = async (stage, installerId) => {
    setSaving(stage);
    try {
      const { data } = await api.put(`/jobs/${job.id}/stage-installer`, {
        stage,
        installer_id: installerId || "",
      });
      const label = subMeta("job", stage).label;
      toast.success(installerId ? `Assigned ${label}` : `Cleared ${label}`);
      onChange?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save assignment");
    } finally {
      setSaving("");
    }
  };

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-3 space-y-2" data-testid={`stage-assignments-${job.id}`}>
      <div className="flex items-center gap-1.5 text-label"><Users className="w-3 h-3" /> Staff per stage</div>
      <div className="space-y-1.5">
        {stages.map((s) => {
          const assigned = map[s.value];
          const isCurrent = s.value === currentSub;
          const options = staff.filter((u) => !u.capable_stages || u.capable_stages.length === 0 || u.capable_stages.includes(s.value));
          const unqualified = staff.filter((u) => u.capable_stages && u.capable_stages.length > 0 && !u.capable_stages.includes(s.value));
          const StageIcon = s.icon;
          return (
            <div
              key={s.value}
              data-testid={`stage-row-${s.value}`}
              className={`flex items-center gap-2 rounded-xl px-2.5 py-2 border ${
                isCurrent
                  ? "bg-blue-500/10 border-blue-400/30"
                  : "bg-black/20 border-white/5"
              }`}
            >
              <StageIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <div className="text-xs text-white flex-1 min-w-0">
                <div className="truncate">{s.label}</div>
                {isCurrent && <div className="text-[9px] uppercase tracking-widest text-blue-300 mt-0.5">Current stage</div>}
              </div>
              {canEdit ? (
                <select
                  data-testid={`stage-picker-${s.value}`}
                  value={(assigned && assigned.id) || ""}
                  disabled={saving === s.value}
                  onChange={(e) => setStage(s.value, e.target.value)}
                  className="bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-[11px] text-white min-w-[140px] focus:outline-none focus:border-white/30"
                >
                  <option value="" className="bg-neutral-900">— unassigned —</option>
                  {options.length > 0 && (
                    <optgroup label="Qualified" className="bg-neutral-900">
                      {options.map((u) => (
                        <option key={u.id} value={u.id} className="bg-neutral-900">
                          {u.name || u.email}{u.role === "assistant" ? " · assistant" : ""}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {unqualified.length > 0 && (
                    <optgroup label="Not trained" className="bg-neutral-900">
                      {unqualified.map((u) => (
                        <option key={u.id} value={u.id} className="bg-neutral-900">
                          {u.name || u.email} (unqualified)
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              ) : (
                <div className="flex items-center gap-1 text-[11px] text-slate-300">
                  <UserIcon className="w-3 h-3 text-slate-500" />
                  {assigned?.name || <span className="text-slate-500 italic">unassigned</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
